import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Dropdown } from '../Dropdown/Dropdown'
import { useId } from '../../hooks/useId'
import styles from './Select.module.css'

const SIZES = ['lg', 'sm']

export function Select({
  size = 'lg',
  label,
  labelIcon,
  optional = false,
  options = [],
  value,
  onChange,
  placeholder = 'Placeholder',
  clearable = true,
  assistiveText,
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  id,
  className = '',
}) {
  const [open, setOpen] = useState(false)
  const [width, setWidth] = useState(undefined)
  const triggerRef = useRef(null)
  const generatedId = useId()
  const fieldId = id || generatedId

  // El dropdown de opciones se arma con el ancho real del trigger (asi
  // en la spec del DS), no con un ancho fijo - se mide el trigger en vez
  // de asumir uno.
  useEffect(() => {
    const el = triggerRef.current
    if (!el) return
    const updateWidth = () => setWidth(el.offsetWidth)
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const isInteractive = !disabled && !readOnly
  const selected = options.find((opt) => opt.value === value)

  const handleSelect = (opt) => {
    onChange?.(opt.value)
    setOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange?.(undefined)
  }

  const containerClasses = [
    styles.select__container,
    styles[`select__container--${size}`],
    error ? styles['select__container--error'] : '',
    disabled ? styles['select__container--disabled'] : '',
    readOnly ? styles['select__container--readonly'] : '',
  ]
    .filter(Boolean)
    .join(' ')

  const textClasses = [styles.select__text, selected ? styles['select__text--filled'] : '']
    .filter(Boolean)
    .join(' ')

  const iconClasses = [styles.select__icon, size === 'sm' ? styles['select__icon--sm'] : '']
    .filter(Boolean)
    .join(' ')

  const trigger = (
    <div
      ref={triggerRef}
      id={fieldId}
      className={containerClasses}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-haspopup="listbox"
    >
      <span className={textClasses}>{selected ? selected.label : placeholder}</span>
      {!readOnly && (
        <div className={styles.select__icons}>
          {clearable && selected && (
            <span
              className={[iconClasses, styles['select__icon--clear'], 'material-icons-outlined'].join(
                ' '
              )}
              onClick={handleClear}
            >
              close
            </span>
          )}
          <div className={styles.select__divider} />
          <span className={[iconClasses, 'material-icons-outlined'].join(' ')}>expand_more</span>
        </div>
      )}
    </div>
  )

  const assistiveContent = error ? (
    <span className={[styles.input__assistive, styles['input__assistive--error']].join(' ')}>
      <span className="material-icons-outlined">error_outline</span>
      {errorMessage}
    </span>
  ) : assistiveText ? (
    <span className={styles.input__assistive}>{assistiveText}</span>
  ) : null

  return (
    <div className={[styles.select, className].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={fieldId} className={styles.input__label}>
          <span
            className={[styles['input__label-text'], styles[`input__label-text--${size}`]].join(' ')}
          >
            {label}
          </span>
          {labelIcon && <span className={styles['input__label-icon']}>{labelIcon}</span>}
          {optional && <span className={styles['input__label-optional']}>(opcional)</span>}
        </label>
      )}
      <Dropdown trigger={trigger} open={open} onOpenChange={setOpen} width={width}>
        {options.map((opt) => (
          <Dropdown.Item key={opt.value} active={opt.value === value} onClick={() => handleSelect(opt)}>
            {opt.label}
          </Dropdown.Item>
        ))}
      </Dropdown>
      {!readOnly && assistiveContent}
    </div>
  )
}

Select.propTypes = {
  size: PropTypes.oneOf(SIZES),
  label: PropTypes.node,
  labelIcon: PropTypes.node,
  optional: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  clearable: PropTypes.bool,
  assistiveText: PropTypes.node,
  error: PropTypes.bool,
  errorMessage: PropTypes.node,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
}
